import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { CheckCircle2, Clock, Mail } from "lucide-react";
import { StepLayout } from "../components/StepLayout";
import { MERCHANT_EXIT_PATH } from "../constants";
import { cn } from "@/utils/classnames";
import { useAuth } from "@/providers/auth";
import { useClientSettings } from "@/queries/client-settings/use-client-settings";
import { useUpdateInvoiceSettings } from "@/queries/client-settings/use-update-invoice-settings";
import { SettingKey, SettingCategory } from "@/queries/client-settings/types";

const Toggle = ({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) => (
  <div className="flex items-center gap-3">
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={cn(
        "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors",
        checked ? "bg-green-500" : "bg-gray-200",
      )}
    >
      <span
        className={cn(
          "inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform",
          checked ? "translate-x-6" : "translate-x-1",
        )}
      />
    </button>
    <span className="text-base font-medium text-gray-700">{label}</span>
  </div>
);

const Step5 = () => {
  const history = useHistory();
  const { cognitoId, clientId, userRole } = useAuth();
  const userId = userRole === "admin" || userRole === "auth_user" ? clientId : cognitoId;

  const { data: settings } = useClientSettings(
    { userId: userId || "", category: "integrations" },
    { enabled: !!userId },
  );

  const deluxeSetting = settings?.find(
    (s) => s.key === SettingKey.DELUXE_INVOICE_DEFAULT_CC,
  );

  const [cardPaymentDefault, setCardPaymentDefault] = useState(true);

  useEffect(() => {
    if (deluxeSetting) {
      setCardPaymentDefault(deluxeSetting.value?.deluxe_invoice_default_cc ?? true);
    }
  }, [deluxeSetting]);

  const { mutate: updateSetting } = useUpdateInvoiceSettings();

  const handleToggle = (value: boolean) => {
    setCardPaymentDefault(value);
    if (!userId) return;
    updateSetting({
      userId,
      payload: {
        category: SettingCategory.INTEGRATIONS,
        key: SettingKey.DELUXE_INVOICE_DEFAULT_CC,
        value: { deluxe_invoice_default_cc: value },
      },
    });
  };

  const handleFinish = () => {
    history.push(MERCHANT_EXIT_PATH);
  };

  return (
    <StepLayout
      step={5}
      isLastStep
      hideBack
      nextLabel="Back to Dashboard"
      onNext={handleFinish}
    >
      <div className="flex flex-col items-center text-center gap-3 py-2">
        <CheckCircle2 className="h-12 w-12 text-green-500" />
        <div>
          <h2 className="text-xl font-bold text-gray-900">
            Application Submitted!
          </h2>
          <p className="text-base font-medium text-gray-500 mt-1">
            Your merchant processing application is under review.
          </p>
        </div>
      </div>

      <div className="rounded-lg bg-gray-50 border border-gray-100 p-4 space-y-3">
        <div className="flex items-start gap-3">
          <Clock className="h-4 w-4 text-gray-400 mt-1 shrink-0" />
          <p className="text-base font-medium text-gray-600 leading-relaxed">
            Applications typically process within{" "}
            <strong className="text-gray-800">1–3 business days</strong>. In
            some cases we may reach out for more information.
          </p>
        </div>
        <div className="flex items-start gap-3">
          <Mail className="h-4 w-4 text-gray-400 mt-1 shrink-0" />
          <p className="text-base font-medium text-gray-600 leading-relaxed">
            You'll receive an{" "}
            <strong className="text-gray-800">email notification</strong> as
            soon as your application has been processed.
          </p>
        </div>
      </div>

      <div>
        <p className="text-base font-semibold text-gray-900 mb-3">
          In the meantime, set up your default card payment settings:
        </p>
        <div className="rounded-lg border border-gray-200 p-4 space-y-3">
          <div>
            <p className="text-base font-semibold text-gray-900 mb-1">
              Default Card Payment Setting
            </p>
            <p className="text-sm font-medium text-gray-500 leading-relaxed">
              When <strong>ON</strong>, card payments are enabled by default on
              invoices and QuickPays but can be turned off per invoice. When{" "}
              <strong>OFF</strong>, card payments are disabled by default but
              can be enabled per invoice.
            </p>
          </div>
          <Toggle
            label="Yes, Accept Card Payments by Default"
            checked={cardPaymentDefault}
            onChange={handleToggle}
          />
        </div>
        <p className="text-sm font-medium text-gray-400 mt-2 leading-relaxed">
          You can update this later under Get Paid &gt; Invoice Settings &gt;
          Merchant Processing.
        </p>
      </div>
    </StepLayout>
  );
};

export default Step5;
